import { useEffect, useState, useCallback } from 'react';
import { ErrorState } from 'types/Payable';
import {
  DepositPayable,
  FormCondition,
  FormConditionCondition,
  FormConditionDayType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { AgxOnOrDaysFrom, ContractDateType } from '@urbanx/agx.ui-components2';

interface FinanceConditionPayableProps {
  id: string;
  onValueChanged: (change: { id: string; value: FormCondition }) => void;
  defaultValue: FormCondition;
  validate?: boolean;
  required?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const conditionsToInclude = [
  FormConditionCondition.OnSpecificDate,
  FormConditionCondition.UponSatisfactionOfSpecialCondition,
];

const FinanceConditionPayable = ({
  id,
  defaultValue,
  onValueChanged,
  validate = false,
  specialConditions,
  campaignId,
}: FinanceConditionPayableProps) => {
  const [selectedCondition, setSelectedCondition] =
    useState<FormConditionCondition>(
      () =>
        defaultValue?.condition ?? FormConditionCondition.NotSubjectToFinance
    );

  const [financeConditionDetails, setFinanceConditionDetails] =
    useState<FormCondition>(() => ({
      ...defaultValue,
      dayType: defaultValue?.dayType ?? FormConditionDayType.Days,
      depositPayable: defaultValue?.depositPayable ?? DepositPayable.NDaysFrom,
    }));

  useEffect(() => {
    if (!financeConditionDetails) return;

    onValueChanged({
      id,
      value: financeConditionDetails,
    });
  }, [financeConditionDetails]);

  useEffect(() => {
    if (!selectedCondition) return;

    setFinanceConditionDetails({
      ...financeConditionDetails,
      condition: selectedCondition,
    });
  }, [selectedCondition]);

  const onConditionChange = useCallback((value: FormCondition) => {
    setFinanceConditionDetails(prev => ({
      ...prev,
      condition: value.condition ?? FormConditionCondition.OnDate,
      daysFromDate: value.daysFromDate,
      depositPayable: value.depositPayable,
      dayType: value.dayType,
      specialConditionId: value.specialConditionId,
      referenceDate: value.referenceDate,
      other: value.other,
    }));
  }, []);

  return (
    <AgxOnOrDaysFrom
      id={id}
      condition={financeConditionDetails}
      conditionsToIncludeInDaysFrom={conditionsToInclude}
      conditionsToIncludeInOn={conditionsToInclude}
      specialConditions={specialConditions}
      campaignId={campaignId}
      validate={validate}
      defaultCondition={FormConditionCondition.OnDate}
      contractDateType={ContractDateType.ContractDate}
      onChange={onConditionChange}
      onIsValidChange={() => {}}
    />
  );
};

export default FinanceConditionPayable;
